import { TOOLS_IDS } from './constants';
import {
  MEASUREMENT_BUFFER,
  adjustTextBoxXForWidthChange,
  expandTextAnnotationToFit,
  getMeasuredBox,
  hasExplicitLineBreaks,
  isTextClippedAtHeight,
  isTextClippedAtWidth,
  isUnwantedWrapAtWidth,
} from './expandTextAnnotationsToFitContent';

const getMaxLineWidth = (textNode) => {
  const lines = textNode.textArr || [];
  if (lines.length === 0) {
    return textNode.getTextWidth();
  }
  return lines.reduce((max, line) => Math.max(max, line.width || 0), 0);
};

/**
 * Shrink (or correct) a text annotation box to hug rendered content.
 * Returns null when no change is needed. Widens instead of shrinking when clipped.
 *
 * @param {object} annotation
 * @returns {object | null}
 */
const fitTextAnnotationToContent = (annotation) => {
  if (annotation?.name !== TOOLS_IDS.TEXT) {
    return null;
  }

  const text = annotation.text;
  if (!text || !String(text).trim()) {
    return null;
  }

  const savedWidth = annotation.width || 0;
  const savedHeight = annotation.height || 0;
  const singleLine = !hasExplicitLineBreaks(text);
  const shouldExpandBox =
    savedWidth > 0 &&
    (isTextClippedAtWidth(annotation, savedWidth) ||
      isTextClippedAtHeight(annotation, savedWidth) ||
      (singleLine && isUnwantedWrapAtWidth(annotation, savedWidth)));

  if (shouldExpandBox) {
    const expanded = expandTextAnnotationToFit(annotation);
    if (
      expanded &&
      (Math.abs((expanded.width || 0) - savedWidth) >= 0.5 ||
        Math.abs((expanded.height || 0) - savedHeight) >= 0.5)
    ) {
      return expanded;
    }
    return null;
  }

  let nextWidth;
  let nextHeight;

  if (singleLine) {
    const natural = getMeasuredBox(annotation);
    nextWidth = natural.width + MEASUREMENT_BUFFER;
    nextHeight = natural.height + MEASUREMENT_BUFFER;
  } else {
    const wrapWidth = savedWidth > 0 ? savedWidth : undefined;
    const wrapped = getMeasuredBox(annotation, wrapWidth);
    const padding = wrapped.textNode.padding() * 2;
    nextWidth = getMaxLineWidth(wrapped.textNode) + padding + MEASUREMENT_BUFFER;
    nextHeight = wrapped.height + MEASUREMENT_BUFFER;
  }

  let nextX = annotation.x ?? 0;
  const nextY = annotation.y ?? 0;

  if (savedWidth > 0) {
    nextX = adjustTextBoxXForWidthChange(
      { ...annotation, width: savedWidth },
      nextWidth,
    );
  }

  if (
    Math.abs(nextWidth - savedWidth) < 0.5 &&
    Math.abs(nextHeight - savedHeight) < 0.5 &&
    Math.abs(nextX - (annotation.x ?? 0)) < 0.5
  ) {
    return null;
  }

  return {
    ...annotation,
    x: nextX,
    y: nextY,
    width: nextWidth,
    height: nextHeight,
  };
};

export default fitTextAnnotationToContent;
